import type { RouteLocation, RouteLocationNormalized, Router } from 'vue-router';
import type { I18n } from 'vue-i18n';
import type { Store } from 'vuex';
import { fetchApi } from '@/hooks/fetch';
import store, { type State } from '../store';
import { canAccess } from '@/utils/user';

const isPublicOnlyRoute = (route: RouteLocationNormalized) => [
  /^\/([^\\/]+\/)?login$/,
  /^\/([^\\/]+\/)?forgot-password$/,
  /^\/([^\\/]+\/)?reset-password\/[^\\/]+$/,
  /^\/([^\\/]+\/)?confirm-email\/[^\\/]+$/,
  /^\/([^\\/]+\/)?register(\/[A-Za-z0-9-_]*)?/,
].some((r) => r.test(route.fullPath));

const canContinue = (_: unknown, to: RouteLocation) => {
  if (isPublicOnlyRoute(to)) {
    return '/';
  }

  return true;
};

export default (router: Router, i18n: I18n) => router.beforeEach(async (to) => {
  const { user } = store.state.auth;
  const loggedIn = store.state.auth.user !== null && store.state.auth.loggedIn;

  if (loggedIn) {
    return canContinue(user, to);
  }

  if (isPublicOnlyRoute(to)) {
    const locale = typeof i18n.global.locale.value === 'string' ? i18n.global.locale.value : 'de';
    return loggedIn ? `/${locale}` : true;
  }

  try {
    const data = await fetchApi('/users/current');

    if (data != null) {
      store.commit('auth/SET_USER', data);
      return canContinue(data, to);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error('Could not fetch user', err, err.message, err.response);
  }

  // Get the locale as a string, fallback to 'de' if it's not set properly
  const locale = typeof i18n.global.locale.value === 'string' ? i18n.global.locale.value : 'de';
  return `/${locale}/login`;
});

export const setupRoleBasedAccess = (router: Router, vuexStore: Store<State>) => router.beforeResolve(async (to) => {
  if (to.meta.roles) {
    const { user } = vuexStore.state.auth;
    if (!user) return true;
    return canAccess(user, to.meta.roles as string[]);
  }

  return true;
});
